import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { fetchTrialAccess } from "../services/trialService";
import type { TrialAccess } from "../types/subscriptions.types";

export type TrialAccessStatus = "loading" | "ready" | "failed";

export interface TrialAccessState {
  /** What the workspace may do today — `null` until the first answer. */
  access: TrialAccess | null;
  status: TrialAccessStatus;
  error?: string;
  reload: () => void;
}

export const TRIAL_ACCESS_QUERY_KEY = ["billing", "trial-access"] as const;

/**
 * Whether this workspace is being shown the trial or the app.
 *
 * One call — `GET /api/v1/billing/subscription/` — read for the one field the
 * sign-up flow turns on: `canStartTrial`. A workspace that may still take the
 * fourtnight is walked through the offer; anyone else goes straight to the
 * dashboard, whether because they are already paying, because their trial is
 * running, or because they have had one.
 *
 * The whole answer is carried rather than that single boolean, because the same
 * call is what the trial's own screens read the running trial's dates from.
 *
 * A failure is reported as one and is not read as "no trial for you": the
 * screens decide what to do with it, and the honest options are to say so and
 * offer a retry, not to silently take an offer away over a request that did not
 * arrive.
 *
 * Backed by TanStack Query: a screen visited again shows the last answer
 * immediately while a fresh read checks it behind the scenes, so this reads as
 * fast on a second visit as everything else in the app now does. A load with
 * nothing cached to fall back on still fails outright — this answer decides
 * which screen somebody is looking at, and a silent guess in its place would
 * risk sending them to the wrong one.
 *
 * Must be used inside a Clerk provider.
 */
export function useTrialAccess(): TrialAccessState {
  const getToken = useTokenGetter();

  const query = useQuery({
    queryKey: TRIAL_ACCESS_QUERY_KEY,
    queryFn: ({ signal }) => fetchTrialAccess(getToken, signal),
  });

  useEffect(() => {
    if (query.isError) {
      console.error("trial access failed to load:", query.error);
    }
  }, [query.isError, query.error]);

  return {
    access: query.data ?? null,
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
    error:
      query.isError && query.error instanceof ApiError
        ? query.error.message
        : query.isError
        ? "We could not reach the server. Check your connection and try again."
        : undefined,
    reload: () => {
      void query.refetch();
    },
  };
}

export default useTrialAccess;
